import { StandardControllerButton } from "../../src/api/controller";
import { Emulator } from "../../src";

const BUTTON_MAP: Record<string, StandardControllerButton> = {
  UP: StandardControllerButton.UP,
  DOWN: StandardControllerButton.DOWN,
  LEFT: StandardControllerButton.LEFT,
  RIGHT: StandardControllerButton.RIGHT,
  A: StandardControllerButton.A,
  B: StandardControllerButton.B,
  START: StandardControllerButton.START,
  SELECT: StandardControllerButton.SELECT,
};

export class TouchController {
  private pointerToButton = new Map<number, StandardControllerButton>();
  private boundHandlers: (() => void)[] = [];
  private turboState = new Map<StandardControllerButton, boolean>();
  private turboIntervals = new Map<StandardControllerButton, number>();
  private turboButtonState = new Map<StandardControllerButton, boolean>();

  constructor(
    private readonly emulator: Emulator,
    private readonly root: HTMLElement
  ) {
    this.registerPointerHandlers();
    this.registerKeyboardHandlers();
    this.registerGlobalHandlers();
    this.registerTurboHandlers();
  }

  public destroy(): void {
    for (const teardown of this.boundHandlers) {
      teardown();
    }
    this.pointerToButton.clear();
    this.stopAllTurbo();
  }

  private registerPointerHandlers(): void {
    const buttons = Array.from(
      this.root.querySelectorAll<HTMLElement>("[data-button]")
    );

    buttons.forEach((element) => {
      element.style.touchAction = "none";
      element.style.webkitUserSelect = "none";
      element.style.userSelect = "none";
      // @ts-ignore - webkit specific property
      element.style.webkitTouchCallout = "none";

      const down = (event: PointerEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const datasetValue = (event.currentTarget as HTMLElement).dataset
          .button;
        if (!datasetValue) {
          return;
        }

        const mapped = BUTTON_MAP[datasetValue];
        if (mapped === undefined) {
          return;
        }

        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        this.pointerToButton.set(event.pointerId, mapped);
        this.updateButton(mapped, true);
      };

      const up = (event: PointerEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const elementTarget = event.currentTarget as HTMLElement;
        if (elementTarget && elementTarget.hasPointerCapture(event.pointerId)) {
          elementTarget.releasePointerCapture(event.pointerId);
        }
        const mapped = this.pointerToButton.get(event.pointerId);
        if (mapped !== undefined) {
          this.updateButton(mapped, false);
          this.pointerToButton.delete(event.pointerId);
        }
      };

      const contextMenu = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
      };

      element.addEventListener("pointerdown", down);
      element.addEventListener("pointerup", up);
      element.addEventListener("pointercancel", up);
      element.addEventListener("pointerleave", up);
      element.addEventListener("contextmenu", contextMenu);

      this.boundHandlers.push(() => {
        element.removeEventListener("pointerdown", down);
        element.removeEventListener("pointerup", up);
        element.removeEventListener("pointercancel", up);
        element.removeEventListener("pointerleave", up);
        element.removeEventListener("contextmenu", contextMenu);
      });
    });
  }

  private registerKeyboardHandlers(): void {
    const keyMap: Record<string, StandardControllerButton> = {
      // Arrow keys (alternative)
      ArrowUp: StandardControllerButton.UP,
      ArrowDown: StandardControllerButton.DOWN,
      ArrowLeft: StandardControllerButton.LEFT,
      ArrowRight: StandardControllerButton.RIGHT,
      // WASD (primary)
      KeyW: StandardControllerButton.UP,
      KeyS: StandardControllerButton.DOWN,
      KeyA: StandardControllerButton.LEFT,
      KeyD: StandardControllerButton.RIGHT,
      // Action buttons
      KeyJ: StandardControllerButton.A,
      KeyK: StandardControllerButton.B,
      // System buttons
      Enter: StandardControllerButton.START,
      Space: StandardControllerButton.SELECT,
    };

    const keyDown = (event: KeyboardEvent) => {
      const mapped = keyMap[event.code];
      if (mapped === undefined) {
        return;
      }
      event.preventDefault();
      this.updateButton(mapped, true);
    };

    const keyUp = (event: KeyboardEvent) => {
      const mapped = keyMap[event.code];
      if (mapped === undefined) {
        return;
      }
      event.preventDefault();
      this.updateButton(mapped, false);
    };

    window.addEventListener("keydown", keyDown, { passive: false });
    window.addEventListener("keyup", keyUp, { passive: false });

    this.boundHandlers.push(() => {
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
    });
  }

  private registerGlobalHandlers(): void {
    // Prevent context menu on all control buttons
    const preventContextMenu = (event: Event) => {
      if ((event.target as HTMLElement).closest("[data-button]")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Release all buttons when visibility changes or app goes to background
    const releaseAllButtons = () => {
      this.pointerToButton.forEach((button) => {
        this.updateButton(button, false);
      });
      this.pointerToButton.clear();
    };

    document.addEventListener("contextmenu", preventContextMenu);
    document.addEventListener("visibilitychange", releaseAllButtons);
    window.addEventListener("blur", releaseAllButtons);
    window.addEventListener("pagehide", releaseAllButtons);

    this.boundHandlers.push(() => {
      document.removeEventListener("contextmenu", preventContextMenu);
      document.removeEventListener("visibilitychange", releaseAllButtons);
      window.removeEventListener("blur", releaseAllButtons);
      window.removeEventListener("pagehide", releaseAllButtons);
    });
  }

  private updateButton(
    button: StandardControllerButton,
    pressed: boolean
  ): void {
    this.emulator.standardController1.updateButton(button, pressed);
    this.emulator.standardController2.updateButton(button, pressed);
  }

  private registerTurboHandlers(): void {
    const turboButtons = Array.from(
      this.root.querySelectorAll<HTMLElement>("[data-turbo]")
    );

    turboButtons.forEach((element) => {
      const click = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const datasetValue = (event.currentTarget as HTMLElement).dataset.turbo;
        if (!datasetValue) {
          return;
        }

        const mapped = BUTTON_MAP[datasetValue];
        if (mapped === undefined) {
          return;
        }

        this.toggleTurbo(mapped, element);
      };

      element.addEventListener("click", click);

      this.boundHandlers.push(() => {
        element.removeEventListener("click", click);
      });
    });
  }

  private toggleTurbo(
    button: StandardControllerButton,
    element: HTMLElement
  ): void {
    const isActive = this.turboState.get(button) ?? false;

    if (isActive) {
      // Stop turbo
      this.stopTurbo(button);
      element.classList.remove("active");
    } else {
      // Start turbo
      this.startTurbo(button);
      element.classList.add("active");
    }
  }

  private startTurbo(button: StandardControllerButton): void {
    // Stop any existing turbo for this button
    this.stopTurbo(button);

    this.turboState.set(button, true);
    this.turboButtonState.set(button, false);

    // Press immediately
    this.updateButton(button, true);
    this.turboButtonState.set(button, true);

    // Set up interval to toggle every 250ms (press for 250ms, release for 250ms = 0.5s cycle)
    const intervalId = window.setInterval(() => {
      const currentState = this.turboButtonState.get(button) ?? false;
      const newState = !currentState;
      this.updateButton(button, newState);
      this.turboButtonState.set(button, newState);
    }, 250);

    this.turboIntervals.set(button, intervalId);
  }

  private stopTurbo(button: StandardControllerButton): void {
    const intervalId = this.turboIntervals.get(button);
    if (intervalId !== undefined) {
      clearInterval(intervalId);
      this.turboIntervals.delete(button);
    }

    // Release the button when stopping turbo
    if (this.turboButtonState.get(button)) {
      this.updateButton(button, false);
    }

    this.turboState.set(button, false);
    this.turboButtonState.set(button, false);
  }

  private stopAllTurbo(): void {
    this.turboIntervals.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this.turboIntervals.clear();
    this.turboState.clear();
    this.turboButtonState.clear();
  }
}
